/**
 * A coluna lateral da Comunicação (pedido de 17/09/2026): clicar no nome do
 * cliente abre, ao lado da conversa, as abas do paciente.
 *
 * Abrir a coluna É abrir a ficha: a leitura passa por `dados().paciente`, que
 * registra o acesso (regra 7), como a tela do paciente. O prontuário só aparece
 * para quem tem a permissão de atendimento.
 *
 * Lead ou número sem cadastro não tem ficha: a coluna diz isso e oferece o
 * caminho (CRM ou cadastrar).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar, Badge, Button, Icon, IconButton } from '@ds/core';
import { Alert, EmptyState } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { dataEHoraPorExtenso, dataSimples, diaIso, idadeEm, moeda } from '@/comuns/formato';
import type { CargaDeAtendimentoECrm } from '@/data/cadastros';
import type { SinaisDaConversa } from '@/data/comunicacao';
import { dados } from '@/data/porta';
import type { CargaDoPaciente, Conversa } from '@/data/tipos';
import { pode } from '@/session/sessao';
import { NovoAgendamentoModal } from '@/screens/NovoAgendamentoModal';
import { NovoContatoModal } from '@/screens/contatos/NovoContatoModal';
import { LinhaDoTempoDoProntuario } from '@/screens/paciente/LinhaDoTempoDoProntuario';
import { OrcamentoAbertoModal } from '@/screens/vendas/OrcamentoAbertoModal';
import { EtiquetasDaConversa } from './EtiquetasDaConversa';

export type AbaDoPainel = 'informacoes' | 'etiquetas' | 'prontuario' | 'agenda' | 'financeiro' | 'orcamentos' | 'documentos';

export interface PainelDoClienteProps {
    conversa: Conversa;
    sinais: SinaisDaConversa | undefined;
    cadastros: CargaDeAtendimentoECrm;
    onFechar: () => void;
    /** Algo mudou (etiqueta, cadastro, agendamento): a caixa recarrega. */
    onMudou: () => void;
}

const SITUACAO_DO_TITULO: Record<string, { rotulo: string; tom: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    pago: { rotulo: 'Pago', tom: 'success' },
    aberto: { rotulo: 'Em aberto', tom: 'warning' },
    parcial: { rotulo: 'Parcial', tom: 'warning' },
    cancelado: { rotulo: 'Cancelado', tom: 'neutral' },
};

function Linha({ rotulo, valor }: { rotulo: string; valor: string | undefined }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '112px minmax(0,1fr)', gap: 8, fontSize: 13 }}>
            <dt style={{ color: 'var(--text-tertiary)' }}>{rotulo}</dt>
            <dd style={{ margin: 0, color: valor ? 'var(--text-primary)' : 'var(--text-tertiary)', overflowWrap: 'anywhere' }}>{valor || '—'}</dd>
        </div>
    );
}

export function PainelDoCliente({ conversa, sinais, cadastros, onFechar, onMudou }: PainelDoClienteProps) {
    const navigate = useNavigate();
    const [aba, setAba] = useState<AbaDoPainel>('informacoes');
    const [carga, setCarga] = useState<CargaDoPaciente | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [lendo, setLendo] = useState(false);
    const [versao, setVersao] = useState(0);
    const [agendando, setAgendando] = useState(false);
    const [cadastrando, setCadastrando] = useState(false);
    const [orcamentoAberto, setOrcamentoAberto] = useState<string | null>(null);

    const ehPaciente = conversa.vinculos.includes('Paciente') && Boolean(conversa.contatoId);
    const ehLead = !ehPaciente && conversa.vinculos.includes('Lead');
    const podeVerProntuario = pode('atendimentos');

    useEffect(() => {
        setCarga(null);
        setErro(null);
        if (!ehPaciente || !conversa.contatoId) return;
        let vivo = true;
        setLendo(true);
        dados().paciente(conversa.contatoId)
            .then((c) => { if (vivo) setCarga(c); })
            .catch((e: unknown) => {
                if (!vivo) return;
                setErro(e instanceof Response ? 'A ficha deste paciente não foi encontrada.' : e instanceof Error ? e.message : 'Não foi possível abrir a ficha.');
            })
            .finally(() => { if (vivo) setLendo(false); });
        return () => { vivo = false; };
    }, [conversa.contatoId, ehPaciente, versao]);

    const recarregar = () => {
        setVersao((v) => v + 1);
        onMudou();
    };

    const abas = [
        { id: 'informacoes', label: 'Informações' },
        { id: 'etiquetas', label: 'Etiquetas', count: sinais?.etiquetas.length ?? 0 },
        ...(ehPaciente && podeVerProntuario ? [{ id: 'prontuario', label: 'Prontuário' }] : []),
        ...(ehPaciente ? [
            { id: 'agenda', label: 'Agenda' },
            { id: 'financeiro', label: 'Financeiro' },
            { id: 'orcamentos', label: 'Orçamentos', count: carga?.orcamentos.length ?? 0 },
            { id: 'documentos', label: 'Documentos', count: carga?.documentos.length ?? 0 },
        ] : []),
    ];
    const abaVisivel = abas.some((a) => a.id === aba) ? aba : 'informacoes';
    const ficha = carga?.ficha;
    const convenio = ficha?.convenioId ? carga?.convenios.find((c) => c.id === ficha.convenioId)?.nome : undefined;

    let corpo;
    if (abaVisivel === 'etiquetas') {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>Etiquetas desta conversa. Servem para filtrar a caixa e disparar automações.</p>
                <EtiquetasDaConversa conversaId={conversa.id} aplicadas={sinais?.etiquetas ?? []} disponiveis={cadastros.etiquetas} onMudou={onMudou} />
                {conversa.vinculos.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Do cadastro:</span>
                        {conversa.vinculos.map((v) => <Badge key={v} tone="neutral">{v}</Badge>)}
                    </div>
                )}
            </div>
        );
    } else if (!ehPaciente) {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Linha rotulo="Canal" valor={conversa.canalNome} />
                    <Linha rotulo="Atendente" valor={conversa.atendente} />
                    <Linha rotulo="Fila" valor={conversa.fila} />
                </dl>
                {ehLead ? (
                    <Alert tone="info" dismissible={false} title="Lead, ainda sem ficha de paciente">
                        Agende a avaliação ou converta no CRM para ver prontuário, agenda e financeiro aqui.
                    </Alert>
                ) : conversa.contatoId ? (
                    <Alert tone="info" dismissible={false} title="Contato sem ficha de paciente">
                        Quando a pessoa virar paciente, a ficha, o prontuário e a agenda aparecem aqui.
                    </Alert>
                ) : (
                    <Alert tone="info" dismissible={false} title="Número sem cadastro">
                        Cadastre a pessoa para guardar os dados e ver a ficha aqui.
                    </Alert>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {ehLead && conversa.contatoId && (
                        <Button size="sm" variant="secondary" iconLeft={<Icon name="kanban" size={14} />} onClick={() => navigate(`/app/contatos/leads?q=${encodeURIComponent(conversa.nome)}`)}>
                            Ver o lead
                        </Button>
                    )}
                    {!conversa.contatoId && (
                        <Button size="sm" iconLeft={<Icon name="user-plus" size={14} />} onClick={() => setCadastrando(true)}>Cadastrar</Button>
                    )}
                </div>
            </div>
        );
    } else if (erro) {
        corpo = <Alert tone="danger" dismissible={false}>{erro}</Alert>;
    } else if (!carga || lendo) {
        corpo = <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Abrindo a ficha…</p>;
    } else if (abaVisivel === 'informacoes' && ficha) {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ficha.alergias && (
                    <Alert tone="warning" dismissible={false} title="Alergias">{ficha.alergias}</Alert>
                )}
                <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Linha rotulo="Telefone" valor={ficha.telefone} />
                    <Linha rotulo="E-mail" valor={ficha.email} />
                    <Linha rotulo="Nascimento" valor={ficha.nascimento ? `${dataSimples(ficha.nascimento)} · ${idadeEm(ficha.nascimento)} anos` : undefined} />
                    <Linha rotulo="CPF" valor={ficha.cpf} />
                    <Linha rotulo="Cidade" valor={[ficha.cidade, ficha.estado].filter(Boolean).join(' · ')} />
                    <Linha rotulo="Convênio" valor={convenio} />
                    <Linha rotulo="Profissional" valor={ficha.profissional} />
                    <Linha rotulo="Origem" valor={ficha.origem} />
                    <Linha rotulo="Cliente desde" valor={ficha.cadastradoEm ? dataSimples(ficha.cadastradoEm) : undefined} />
                </dl>
                {ficha.observacoes && (
                    <p style={{ margin: 0, padding: 10, borderRadius: 'var(--radius-md)', background: 'var(--surface-secondary)', fontSize: 13, whiteSpace: 'pre-wrap' }}>{ficha.observacoes}</p>
                )}
                <Button size="sm" variant="secondary" iconLeft={<Icon name="external-link" size={14} />} onClick={() => navigate(`/app/contatos/${conversa.contatoId ?? ''}`)}>
                    Abrir ficha completa
                </Button>
            </div>
        );
    } else if (abaVisivel === 'prontuario' && conversa.contatoId) {
        corpo = (
            <LinhaDoTempoDoProntuario
                contatoId={conversa.contatoId} prontuario={carga.prontuario}
                onImprimir={(fichaId) => navigate(`/app/contatos/${conversa.contatoId ?? ''}/prontuario?ficha=${encodeURIComponent(fichaId)}`)}
            />
        );
    } else if (abaVisivel === 'agenda') {
        const proximo = sinais?.proximoAgendamento;
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {proximo ? (
                    <div style={{ padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>Próxima consulta</p>
                        <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 600 }}>{dataEHoraPorExtenso(proximo.inicioEm)}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>{proximo.procedimento} · {proximo.profissional}</p>
                    </div>
                ) : (
                    <EmptyState compact icon="calendar-x" title="Sem consulta marcada" description="Agende direto daqui, sem sair da conversa." />
                )}
                <Button size="sm" iconLeft={<Icon name="calendar-plus" size={14} />} onClick={() => setAgendando(true)}>Agendar</Button>
                {sinais?.atendimentoDeHojeId && (
                    <Button size="sm" variant="secondary" iconLeft={<Icon name="stethoscope" size={14} />} onClick={() => navigate(`/app/atendimentos/editor/${sinais.atendimentoDeHojeId ?? ''}`)}>
                        Abrir a consulta de hoje
                    </Button>
                )}
                {carga.prontuario.atendimentos.length > 0 && (
                    <div>
                        <p style={{ margin: '4px 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Consultas</p>
                        <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                            {carga.prontuario.atendimentos.slice(0, 8).map((a) => <li key={a.id}>{a.rotulo}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        );
    } else if (abaVisivel === 'financeiro') {
        const f = carga.financeiro;
        const abertos = carga.titulos.filter((t) => t.situacao !== 'pago' && t.situacao !== 'cancelado');
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Linha rotulo="Investido" valor={moeda(f.totalInvestidoCentavos)} />
                    <Linha rotulo="Em aberto" valor={moeda(f.emAbertoCentavos)} />
                    <Linha rotulo="Vencido" valor={moeda(f.vencidoCentavos)} />
                    {f.pacote && <Linha rotulo="Pacote" valor={`${f.pacote.nome} · ${f.pacote.sessoesConcluidas} de ${f.pacote.sessoesTotais}`} />}
                </dl>
                {abertos.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nada em aberto.</p>
                ) : (
                    <ul aria-label="Títulos em aberto" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {abertos.map((t) => {
                            const s = SITUACAO_DO_TITULO[t.situacao] ?? { rotulo: t.situacao, tom: 'neutral' as const };
                            return (
                                <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.descricao}</span>
                                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>vence {dataSimples(t.vencimento)}</span>
                                    </span>
                                    <span style={{ textAlign: 'right' }}>
                                        <span style={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>{moeda(t.valorCentavos - t.pagoCentavos)}</span>
                                        <Badge tone={s.tom} dot>{s.rotulo}</Badge>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    } else if (abaVisivel === 'orcamentos') {
        corpo = carga.orcamentos.length === 0 ? (
            <EmptyState compact icon="receipt" title="Nenhum orçamento" description="Os orçamentos do paciente aparecem aqui." />
        ) : (
            <ul aria-label="Orçamentos" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {carga.orcamentos.map((o) => (
                    <li key={o.id}>
                        <button
                            type="button" onClick={() => setOrcamentoAberto(o.id)} aria-label={`Abrir ${o.numero}`}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-primary)' }}
                        >
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontWeight: 600 }}>{o.numero}</span>
                                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.resumo}</span>
                            </span>
                            <span style={{ textAlign: 'right' }}>
                                <span style={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>{moeda(o.totalCentavos)}</span>
                                <Badge tone={o.status === 'aprovado' ? 'success' : o.status === 'recusado' ? 'danger' : 'neutral'} dot>{o.status}</Badge>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    } else if (abaVisivel === 'documentos') {
        corpo = carga.documentos.length === 0 ? (
            <EmptyState compact icon="file-text" title="Nenhum documento" description="Termos, atestados e receitas do paciente aparecem aqui." />
        ) : (
            <ul aria-label="Documentos" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {carga.documentos.map((d) => (
                    <li key={d.id}>
                        <button
                            type="button"
                            onClick={() => navigate(`/app/documentos?q=${encodeURIComponent(d.titulo)}&documento=${encodeURIComponent(d.id)}`)}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-primary)' }}
                        >
                            <Icon name="file-text" size={14} />
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.titulo}</span>
                                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{d.tipo} · {dataSimples(d.atualizadoEm.slice(0, 10))}</span>
                            </span>
                            <Badge tone="neutral">{d.status}</Badge>
                        </button>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <aside
            aria-label={`Cliente: ${conversa.nome}`}
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px 8px 14px' }}>
                <Avatar name={conversa.nome} size="md" />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conversa.nome}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {ehPaciente ? 'Paciente' : ehLead ? 'Lead' : 'Sem cadastro'}
                        {ficha?.nascimento ? ` · ${idadeEm(ficha.nascimento)} anos` : ''}
                    </p>
                </div>
                <IconButton icon={<Icon name="x" size={16} />} label="Fechar dados do cliente" onClick={onFechar} />
            </div>
            <div style={{ padding: '0 8px' }}>
                <Tabs ariaLabel="Dados do cliente" activeId={abaVisivel} onChange={(id) => setAba(id as AbaDoPainel)} tabs={abas} />
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14 }}>{corpo}</div>

            {agendando && conversa.contatoId && (
                <NovoAgendamentoModal
                    open onClose={() => setAgendando(false)} dataPadrao={diaIso()}
                    pacientePadrao={{ id: conversa.contatoId, nome: conversa.nome }}
                    onCriado={recarregar}
                />
            )}
            <NovoContatoModal
                open={cadastrando} tipo="paciente" nomeInicial={conversa.nome}
                onClose={() => setCadastrando(false)} onCriado={onMudou}
            />
            {orcamentoAberto && (
                <OrcamentoAbertoModal orcamentoId={orcamentoAberto} onFechar={() => setOrcamentoAberto(null)} onMudou={recarregar} />
            )}
        </aside>
    );
}
